import { useContext } from 'react';
import { useFragment, graphql } from 'react-relay';
import { utils, BigNumber } from 'ethers';
import { NftPdpBidModalContext } from '../NftPdpBidModal/NftPdpBidModalContext';
import { NftPdpContractContext } from '../NftPdpContractContext/NftPdpContractContext';
import { roundUpDecimal } from 'dibs-crypto-utils/exports/formattingUtils';
import { type useBidPrices_nftAuction$key } from './__generated__/useBidPrices_nftAuction.graphql';

export function useBidPrices({
    nftAuction: nftAuctionRef,
}: {
    nftAuction: useBidPrices_nftAuction$key | null | undefined;
}): {
    baseEther?: string | null;
    baseWei?: string | null;
    minEther: number;
    minWei: BigNumber;
} {
    const { currentBidWei } = useContext(NftPdpBidModalContext);
    const { minBidIncrement, isV2AuctionContract } = useContext(NftPdpContractContext);
    const nftAuction = useFragment(
        graphql`
            fragment useBidPrices_nftAuction on NftAuction {
                reservePrice {
                    ether
                    wei
                }
            }
        `,
        nftAuctionRef
    );
    if (!nftAuction) {
        return {
            minEther: 0,
            minWei: BigNumber.from(0),
        };
    }
    const { reservePrice } = nftAuction || {};

    let baseEther: string | null | undefined;
    let baseWei: string | null | undefined;
    let minEther: number;
    let minWei: BigNumber;

    if (currentBidWei) {
        baseWei = currentBidWei;
        baseEther = utils.formatEther(currentBidWei);
        minWei = BigNumber.from(baseWei)
            .mul(minBidIncrement || 0)
            .div(isV2AuctionContract ? 10000 : 100)
            .add(baseWei || 0);

        minEther = roundUpDecimal(parseFloat(utils.formatEther(minWei)), 4);
    } else {
        ({ ether: baseEther, wei: baseWei } = reservePrice || {});
        minWei = BigNumber.from(baseWei);
        minEther = roundUpDecimal(parseFloat(utils.formatEther(minWei)), 4);
    }

    return {
        baseEther,
        baseWei,
        minEther,
        minWei,
    };
}
